import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe, QrCode, BarChart3, Palette, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { qrTemplates } from '../data/qrTemplates';
import { QRCodeSVG } from 'qrcode.react';

const LandingPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Logo className="h-10 w-10" data-testid="logo-icon" />
            <span className="text-2xl font-heading font-bold text-primary" data-testid="brand-name">cyQRus</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login" data-testid="header-login-link">
              <Button variant="ghost" className="text-primary hover:text-accent">{t('login')}</Button>
            </Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="btn-accent">{t('register')}</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6" data-testid="hero-content">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-primary leading-tight">
                {t('hero_title')}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {t('hero_subtitle')}
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/register" data-testid="hero-get-started-btn">
                  <Button className="btn-accent h-12 px-8">
                    {t('get_started')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#features" data-testid="hero-learn-more-btn">
                  <Button variant="outline" className="h-12 px-8">{t('learn_more')}</Button>
                </a>
              </div>
            </div>
            <div className="relative" data-testid="hero-image">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-accent/10 to-primary/5 flex items-center justify-center p-12">
                <QrCode className="w-full h-full text-accent opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="features-section">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary text-center mb-12">
            {t('features_title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-6 space-y-4 hover:-translate-y-1 transition-transform" data-testid="feature-card-1">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <QrCode className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary">{t('feature1_title')}</h3>
              <p className="text-sm text-muted-foreground">{t('feature1_desc')}</p>
            </div>
            <div className="card p-6 space-y-4 hover:-translate-y-1 transition-transform" data-testid="feature-card-2">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Palette className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary">{t('feature2_title')}</h3>
              <p className="text-sm text-muted-foreground">{t('feature2_desc')}</p>
            </div>
            <div className="card p-6 space-y-4 hover:-translate-y-1 transition-transform" data-testid="feature-card-3">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary">{t('feature3_title')}</h3>
              <p className="text-sm text-muted-foreground">{t('feature3_desc')}</p>
            </div>
            <div className="card p-6 space-y-4 hover:-translate-y-1 transition-transform" data-testid="feature-card-4">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Globe className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-primary">{t('feature4_title')}</h3>
              <p className="text-sm text-muted-foreground">{t('feature4_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="templates" className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-primary-50 to-white" data-testid="templates-section">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-4">
              {t('templates_title')}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              {t('templates_subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {qrTemplates.slice(0, 8).map((template) => (
              <div 
                key={template.id} 
                className="card p-6 hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`template-card-${template.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl">{template.icon}</span>
                  <span className="text-xs px-2 py-1 bg-secondary rounded-full text-muted-foreground">
                    {template.category}
                  </span>
                </div>
                
                <div className="flex justify-center mb-4">
                  <div 
                    className="p-3 rounded-xl transition-transform group-hover:scale-105"
                    style={{ backgroundColor: template.customization.bgColor }}
                  >
                    <QRCodeSVG
                      value={template.placeholder || 'https://cyqrus.com'}
                      size={80}
                      fgColor={template.customization.fgColor}
                      bgColor={template.customization.bgColor}
                      level="M"
                    />
                  </div>
                </div>
                
                <h3 className="text-lg font-heading font-semibold text-primary mb-1">
                  {template.name}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {template.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/register" data-testid="view-all-templates-btn">
              <Button className="btn-accent h-12 px-8">
                {t('get_started')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-12 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-sm">© 2026 cyQRus. Dynamic smart QR codes for Cyprus businesses.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;